import { memo } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { useDeviceType } from '@/utils'
import { useTranslations } from '@/core/translation'
import {
  BottomDrawer,
  TIME_RANGE_SLIDER_DEFAULT_MAX,
  TIME_RANGE_SLIDER_DEFAULT_MIN,
  TimeRangeSlider,
  TimeRangeSliderDropdown,
} from '@/components/Surface'
import { FilterChip } from '@/components/Utils'
import { useTimeRangeFilterResponsiveController } from './TimeRangeFilterResponsive.controller'
import { TimeRangeFilterResponsiveProps } from './TimeRangeFilterResponsive.types'

function TimeRangeFilterResponsive({ hide, onChange, ...props }: TimeRangeFilterResponsiveProps) {
  const { isMobile } = useDeviceType()

  const hasValue = props.value.min !== props.min || props.value.max !== props.max
  const diffBetweenMinMax = Math.abs((props.min || 0) - (props.max ?? 0))
  const onChangeDebounced = useDebouncedCallback(onChange, 150)

  if (hide || diffBetweenMinMax < 2) return null

  if (isMobile) return <TimeRangeFilterMobile onChange={onChange} {...props} />

  return <TimeRangeSliderDropdown isSelected={hasValue} onChange={onChangeDebounced} {...props} />
}

function TimeRangeFilterMobile(props: TimeRangeFilterResponsiveProps) {
  const { onClear, min, max, label, durationLabel } = props
  const t = useTranslations('components')

  const {
    displayLabel,
    hasValue,
    isDrawerOpen,
    setIsDrawerOpen,
    closeDrawer,
    onApply,
    intermediateValue,
    setIntermediateValue,
  } = useTimeRangeFilterResponsiveController(props)

  return (
    <>
      <FilterChip
        label={displayLabel}
        isActive={hasValue}
        onClick={() => setIsDrawerOpen(true)}
        onClear={onClear}
      />

      <BottomDrawer
        open={isDrawerOpen}
        onOpen={() => {}}
        onClose={closeDrawer}
        heading={label}
        actionButtonProps={{
          primaryActionText: t('filters.apply'),
          onPrimaryActionClick: onApply,
          secondaryActionText: t('filters.clear'),
          onSecondaryActionClick: () => {
            setIntermediateValue({
              min: min ?? TIME_RANGE_SLIDER_DEFAULT_MIN,
              max: max ?? TIME_RANGE_SLIDER_DEFAULT_MAX,
            })
          },
        }}>
        <TimeRangeSlider
          showTitle={false}
          min={min}
          max={max}
          value={intermediateValue}
          durationLabel={durationLabel}
          onChange={setIntermediateValue}
        />
      </BottomDrawer>
    </>
  )
}

export default memo(TimeRangeFilterResponsive)
